import StationEquipmentSection from '../../StationEquipmentSection/StationEquipmentSection'
import StationLotsTable from '../../StationLotsTable/StationLotsTable'
import { StationApi } from 'api/StationApi'
import clsx from 'clsx'
import { format } from 'date-fns'
import { ReactComponent as GearSvg } from 'images/icons/gear.svg'
import { ReactComponent as NoConnectionSvg } from 'images/icons/signal_wifi_off.svg'
import { ReactComponent as ConnectionSvg } from 'images/icons/signal_wifi_on.svg'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectStation } from 'store/selectors/station'
import { TIMEOUT_FETCHING_STATUS } from 'utils/constants'
import { DIAGNOSTIC_LEVEL, getDiagnosticStatusText } from 'utils/diagnosticsUtils'
import {
  formatInventoryMismatchTitle,
  getStationFeedbackStatus,
  stationFeedbackLabel,
  stationFeedbackTitle,
} from 'utils/stationFeedback'
import { formatStationErrorMsg } from 'utils/stationErrorMsg'

import style from '../StationDetails.module.css'

const StationInfo = () => {
  const station = useSelector(selectStation)
  const feedback = getStationFeedbackStatus(station)
  const isOnline = feedback === 'online'
  const isOffline = feedback === 'offline'
  const isNoFeedback = feedback === 'no_feedback'
  const statusTimestamp = station.lastTelemetryAt || station.updatedAt
  const statusTimeMs = statusTimestamp ? new Date(statusTimestamp).getTime() : null
  const mismatchTitle = formatInventoryMismatchTitle(station)
  const [stationError, setStationError] = useState<{
    error_code: number
    error_msg: string
  } | null>(null)

  useEffect(() => {
    if (!isOnline || !station.id) {
      setStationError(null)
      return
    }
    let cancelled = false
    const load = async () => {
      try {
        const response = await StationApi.fetchTelemetrySnapshot(station.id)
        if (cancelled) return
        const err = response.snapshot?.error
        const code = Number(err?.error_code ?? 0)
        if (!err || code === 0) {
          setStationError(null)
          return
        }
        setStationError({
          error_code: code,
          error_msg: String(err.error_msg || '').trim(),
        })
      } catch {
        /* keep last good diagnostics */
      }
    }
    load()
    const timer = window.setInterval(load, TIMEOUT_FETCHING_STATUS)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [station.id, isOnline])

  const diagnosticsLevel = stationError ? DIAGNOSTIC_LEVEL.ERROR : DIAGNOSTIC_LEVEL.OK
  const diagnosticsDetail =
    diagnosticsLevel === DIAGNOSTIC_LEVEL.OK
      ? '-'
      : formatStationErrorMsg(stationError?.error_msg) || '-'

  return (
    <div className={style.root}>
      {mismatchTitle && (
        <div className={style.mismatchBanner} title={mismatchTitle}>
          Station info mismatch — configured lots differ from last telemetry report.
        </div>
      )}
      <div
        className={clsx(style.tableRow, isOffline && style.offlineRow)}
        title={stationFeedbackTitle(feedback)}
      >
        <div>
          {isOnline ? (
            <ConnectionSvg className={style.connectionIcon} />
          ) : isNoFeedback ? null : (
            <NoConnectionSvg className={style.connectionIcon} />
          )}
        </div>
        <div className={style.textAlignLeft}>Connection</div>
        <div className={style.centered}>{isNoFeedback ? 'N/A' : stationFeedbackLabel(feedback)}</div>
        {isOnline && (
          <div>
            <div className={style.percents}>
              {statusTimeMs != null ? format(new Date(statusTimeMs), 'dd LLLL yyyy') : '-'}
            </div>
            <div className={style.percents}>
              {statusTimeMs != null ? format(new Date(statusTimeMs), 'HH:mm:ss') : '-'}
            </div>
          </div>
        )}
        {isOffline && (
          <div>
            <div className={style.percents}>Last telemetry</div>
            <div className={style.percents}>
              {statusTimeMs != null
                ? format(new Date(statusTimeMs), 'dd LLLL yyyy HH:mm:ss')
                : '-'}
            </div>
          </div>
        )}
        {isNoFeedback && (
          <div className={style.percents}>Without live status by design</div>
        )}
      </div>
      {isOnline && (
        <div
          className={clsx(
            style.tableRow,
            style.diagnosticsRow,
            diagnosticsLevel === DIAGNOSTIC_LEVEL.ERROR && style.errorRow
          )}
          title={
            stationError
              ? formatStationErrorMsg(stationError.error_msg) || undefined
              : undefined
          }
        >
          <div>
            <GearSvg />
          </div>
          <div className={style.textAlignLeft}>Diagnostics</div>
          <div className={clsx(style.centered, style.diagnosticsWrap)}>
            {getDiagnosticStatusText(diagnosticsLevel)}
          </div>
          <div className={clsx(style.percents, style.diagnosticsWrap)}>{diagnosticsDetail}</div>
        </div>
      )}
      {!isNoFeedback && <StationLotsTable />}
      <StationEquipmentSection />
    </div>
  )
}

export default StationInfo
